<template>
  <div style="display: flex; align-items: center; gap: 4px;">
    <i v-if=" !disabled && withTooltip" class="mdi mdi-information-outline" style="opacity: 0.6;"
       v-tooltip.top="'To enter values manually, use space or comma'"/>
    <div class="calendar-wrapper" ref="wrapper">
      <div :class="`calendar-input ${disabled ? 'disabled' : ''}`" ref="input" @click="toggleDropdown">
        <InputText
            v-if="allowInput"
            class="calendar-text"
            v-model="input"
            :placeholder="placeholder"
            :style="inputStyle"
            style="background: transparent;"
            @input="onInputChange"
            @focus="onInputFocus"
            @blur="onInputBlur"
            :disabled="disabled"
            autocomplete="off"
        />
        <span v-else class="calendar-text"
              :style="`${modelValue.length && modelValue.length !== items.length ? 'color: #333333' : ''}`">
        {{ displaySelected }}
      </span>
        <span class="calendar-icon">
        <Button
            v-if="modelValue.length"
            icon="mdi mdi-close"
            class="p-button-text p-button-danger"
            @click.stop="clearSelection"
        />
        <i class="pi pi-chevron-down" style="font-size: 12px;"/>
      </span>
      </div>

      <Teleport to="body">
        <div v-if="dropdownOpen" class="calendar-dropdown" :style="dropdownStyles">
          <div style="margin: 0 0 8px 4px; display: flex; align-items: center; gap: 8px;">
            <Checkbox v-model="isAllSelected" binary/>
            <span style="color: #6c757d;">Select all</span>
          </div>
          <div class="grid" :style="{ gridTemplateColumns: `repeat(${columns}, 1fr)` }">
            <div
                v-for="(item, index) in items"
                :key="index"
                class="cell"
                :class="{ selected: internalValueIndexes.includes(index) }"
                @click.stop="toggleItem(index)"
            >
              {{ item }}
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<script>
export default {
  name: "CalendarMultiSelect",
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "Every item",
    },
    items: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Number,
      default: 7,
    },
    withTooltip: {
      type: Boolean,
      default: false,
    },
    allowInput: {
      type: Boolean,
      default: false,
    }
  },
  emits: ["update:modelValue"],
  data() {
    return {
      dropdownOpen: false,
      dropdownStyles: {
        top: "0px",
        left: "0px",
        position: "absolute",
      },
      isAllSelected: false,
      input: "",
      isInputFocused: false,
    };
  },
  computed: {
    internalValueIndexes() {
      return this.modelValue
          .map((value) => this.items.indexOf(value))
          .filter((index) => index !== -1);
    },
    displaySelected() {
      if (!this.modelValue.length) return this.placeholder;
      if (this.modelValue.length === this.items.length) return this.placeholder;

      const sorted = [...this.modelValue]
          .map((val) => this.items.indexOf(val))
          .filter((i) => i !== -1)
          .sort((a, b) => a - b);

      const ranges = [];

      let start = sorted[0];
      let end = sorted[0];

      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === end + 1) {
          end = sorted[i];
        } else {
          ranges.push(
              start === end
                  ? `${this.items[start]}`
                  : `${this.items[start]} - ${this.items[end]}`
          );
          start = end = sorted[i];
        }
      }

      ranges.push(
          start === end ? `${this.items[start]}` : `${this.items[start]} - ${this.items[end]}`
      );
      return ranges.join(", ");
    },
    inputStyle() {
      return this.modelValue.length && this.modelValue.length !== this.items.length
          ? "color: #333333"
          : "";
    },
  },
  watch: {
    isAllSelected(value) {
      if (value) {
        this.$emit("update:modelValue", [...this.items]);
      } else {
        this.$emit("update:modelValue", []);
      }
    },
    modelValue: {
      immediate: true,
      handler(val) {
        if (!this.isInputFocused) {
          this.input = val.length === this.items.length ? "" : val.join(", ");
        }
        this.isAllSelected = val.length === this.items.length;
      },
    },
  },
  methods: {
    clearSelection() {
      this.isInputFocused = false;
      this.$emit("update:modelValue", []);
      this.isAllSelected = false;
      this.input = "";
    },
    toggleDropdown() {
      if (this.disabled) return;
      this.dropdownOpen = !this.dropdownOpen;

      this.$nextTick(() => {
        if (this.dropdownOpen && this.$refs.input) {
          const rect = this.$refs.input.getBoundingClientRect();
          this.dropdownStyles.top = `${rect.bottom + window.scrollY}px`;
          this.dropdownStyles.left = `${rect.left + window.scrollX}px`;
          this.dropdownStyles.width = `${rect.width}px`;
        }
      });
    },
    toggleItem(index) {
      const selectedIndexes = [...this.internalValueIndexes];
      const i = selectedIndexes.indexOf(index);
      if (i === -1) {
        selectedIndexes.push(index);
      } else {
        selectedIndexes.splice(i, 1);
      }
      const selectedValues = selectedIndexes.map((i) => this.items[i]);
      this.$emit("update:modelValue", selectedValues);
    },
    handleClickOutside(e) {
      setTimeout(() => {
        const wrapper = this.$refs.wrapper;
        const dropdown = document.querySelector(".calendar-dropdown");

        if (
            wrapper &&
            !wrapper.contains(e.target) &&
            dropdown &&
            !dropdown.contains(e.target)
        ) {
          this.dropdownOpen = false;
          this.isInputFocused = false;
        }
      }, 0);
    },
    onInputChange() {
      const rawParts = this.input.split(/[,\s]+/).map(s => s.trim());
      const selectedValues = [];

      rawParts.forEach(part => {
        const numPart = Number(part);
        if (!isNaN(numPart)) {
          const match = this.items.find(item => Number(item) === numPart);
          if (match !== undefined && !selectedValues.includes(match)) {
            selectedValues.push(match);
          }
        }
      });

      this.$emit("update:modelValue", selectedValues);
      this.isAllSelected = selectedValues.length === this.items.length;
    },
    onInputFocus() {
      this.isInputFocused = true;
    },
    onInputBlur() {
      this.isInputFocused = false;
      if (!this.allowInput) return;

      if (this.modelValue.length === this.items.length || this.modelValue.length === 0) {
        this.input = "";
      } else {
        this.input = this.modelValue.join(", ");
      }
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>


<style scoped>
.calendar-wrapper {
  position: relative;
  width: 100%;
  height: 30px;
}

.calendar-input {
  border: 1px solid #ccc;
  padding: 7px;
  cursor: pointer;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-family: Roboto, 'Roboto Light', sans-serif;
  height: 30px;
  color: #6c757d;
}

.calendar-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
  padding-left: 0;
  border: none;
}

.calendar-text:focus {
  outline: none;
  box-shadow: none;
}

.calendar-icon {
  margin-left: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6c757d;
}

.calendar-dropdown {
  position: absolute;
  z-index: 1100;
  background: white;
  border: 1px solid #ccc;
  margin-top: 4px;
  padding: 8px;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
}

.cell {
  text-align: center;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  background: #f9f9f9;
  transition: background 0.2s;
  font-size: 12px;
  user-select: none;
}

.cell:hover {
  background: #eee;
}

.cell.selected {
  background: #82B541;
  color: white;
}

.disabled {
  cursor: default !important;
  pointer-events: none;
  opacity: 0.6;
}
</style>
